import React, { useRef } from "react"
import styled from "styled-components"
import { getCssSizeValue, getFontSize, getHtmlFromRedactorField } from "@/utils/utils"
import GridContainer from "@/components/common/GridContainer"
import { motion } from "framer-motion"
import CommonButton from "@/components/common/Buttons/Button"
import { BREAKPOINTS } from "@/styles/themeSettings"
import { BUTTON_TYPE } from "@/constants"
import useScrollContext from "@/hooks/useScrollContext"
import Text from "@/components/common/Text"

const StyledWrapper = styled.div`
	border-top: 1px solid ${({ theme }) => theme.colors.richBlack};

	&:last-child {
		border-bottom: 1px solid ${({ theme }) => theme.colors.richBlack};
	}

	transition: background var(--rgr-duration) var(--ease);

	&.open,
	&:hover {
		background: ${({ theme }) => theme.colors.platinum};
	}
`

const StyledQuestionWrapper = styled(GridContainer)`
	cursor: pointer;
	align-items: flex-start;
	position: relative;
	padding: ${getCssSizeValue(16, BREAKPOINTS.MOBILE)} 0;
	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		padding: ${getCssSizeValue(48)} 0;
		position: static;
		align-items: center;
	}

	.question-number {
		font-size: ${getFontSize(18, BREAKPOINTS.MOBILE)};
		line-height: ${getFontSize(18, BREAKPOINTS.MOBILE)};
		font-weight: ${({ theme }) => theme.fontWeight.bold};
		@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
			font-size: ${getFontSize(22, BREAKPOINTS.TABLET)};
			line-height: ${getFontSize(22, BREAKPOINTS.TABLET)};
		}
		@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
			font-size: ${getFontSize(22, BREAKPOINTS.DESKTOP)};
			line-height: ${getFontSize(26, BREAKPOINTS.DESKTOP)};
		}
	}
`

const StyledFaqAnswer = styled(motion.div)`
	height: 0;
	padding-bottom: 0;
	overflow-y: hidden;
`

const IconWrapper = styled.div`
	display: flex;
	justify-content: flex-end;
	position: absolute;
	top: 0;
	right: calc(-1 * var(--paddingX));
	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		position: static;
		top: unset;
		right: unset;
	}
`

const Button = styled(CommonButton)`
	--color: unset;
	--bg-color: unset;
	--stick-size: 2px;
	--size: ${getCssSizeValue(30, BREAKPOINTS.MOBILE)};
	margin-left: auto;
	position: relative;
	width: var(--size);
	min-height: var(--size);
	transition: background-color var(--rgr-duration) var(--ease);
	background-color: var(--bg-color) !important;

	&:before,
	&:after {
		content: "";
		position: absolute;
		left: 50%;
		top: 50%;
		translate: -50% -50%;
		background-color: var(--color);
		transition: background-color var(--rgr-duration) var(--ease);
	}

	&:before {
		width: var(--stick-size);
		height: 50%;
		transition: rotate var(--rgr-duration) var(--ease);
	}

	&:after {
		width: 50%;
		height: var(--stick-size);
	}

	&.expand {
		--color: ${({ theme }) => theme.colors.white};
		--bg-color: ${({ theme }) => theme.colors.chilliRed};
	}

	&.hide {
		--color: ${({ theme }) => theme.colors.chilliRed};
		--bg-color: ${({ theme }) => theme.colors.white};

		&:before {
			rotate: -90deg;
		}
	}

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.TABLET]}px) {
		--size: ${getCssSizeValue(50, BREAKPOINTS.TABLET)};
	}

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		--size: ${getCssSizeValue(50)};
	}
`

const variants = {
	open: (height) => ({
		height: height + 40,
		opacity: 1,
		transition: {
			duration: 0.3,
		},
	}),
	closed: {
		height: 0,
		opacity: 0,
		transition: {
			duration: 0.2,
		},
	},
}

const SingleQuestionAndAnswer = ({ question, answer, index, activeIndex, handleUpdateActiveItem }) => {
	const answerRef = useRef(null)
	const isOpen = index === activeIndex
	const contentHeight = answerRef.current?.scrollHeight || 0
	const scroll = useScrollContext()
	const renderPrefix = index + 1 < 10
	const handleClick = () => {
		const newIndex = index === activeIndex ? null : index
		handleUpdateActiveItem(newIndex)
		scroll.refresh(300) //animation duration
	}

	return (
		<StyledWrapper className={`${isOpen ? "open" : ""}`}>
			<StyledQuestionWrapper className="indent-desktop-1" onClick={handleClick}>
				<div className="question-number typo-borna typo-borna-30 col-desktop-1 col-mobile-1">
					{renderPrefix && 0}
					{index + 1}
				</div>
				<div
					className="question typo-borna typo-borna-37 col-desktop-9 col-tablet-6 col-mobile-6"
					dangerouslySetInnerHTML={getHtmlFromRedactorField(question)}
				/>
				<div className="col-desktop-3" />
				<IconWrapper className="col-desktop-1 col-mobile-1">
					<Button
						type={BUTTON_TYPE.UNSTYLED}
						// onClick={handleOnClick}
						className={`${isOpen ? "hide" : "expand"}`}
						aria-label="click to expand"
						animation={false}
						noHoverEffect
					/>
				</IconWrapper>
			</StyledQuestionWrapper>
			<StyledFaqAnswer
				ref={answerRef}
				variants={variants}
				initial="closed"
				animate={isOpen ? "open" : "closed"}
				custom={contentHeight}
			>
				<GridContainer className="indent-desktop-2">
					<Text
						dangerouslySetInnerHTML={getHtmlFromRedactorField(answer)}
						className="typo-strawford typo-strawford-96 col-desktop-9 col-mobile-8"
					/>
				</GridContainer>
			</StyledFaqAnswer>
		</StyledWrapper>
	)
}

export default SingleQuestionAndAnswer
